/* Sequoia Fitness | Website: https://www.sequoia.fitness | Author: Ali Hosseini | Copyright (c) 2026 sequoia.fitness. All rights reserved. */
/* Designed and developed by Pouria Shabani | https://linkedin.com/in/pouriashabani */

import { CHART, REPS, RPE_ROWS, RIR_LABEL, pct } from './rpeData.js'

const W = 640
const H = 360
const M = { l: 46, r: 64, t: 18, b: 40 }
const Y_MIN = 58
const Y_MAX = 100
const x = (reps) => M.l + ((reps - 1) / 11) * (W - M.l - M.r)
const y = (p) => M.t + ((Y_MAX - p) / (Y_MAX - Y_MIN)) * (H - M.t - M.b)
const hue = (rpe) => 350 - ((10 - rpe) / 4) * 140

const lineColor = (rpe) => 'hsl(' + hue(rpe) + ' 78% 46%)'
const cellColor = (p) => 'hsl(' + (20 + ((p - 61) / 39) * 120) + ' 85% ' + (88 - ((p - 61) / 39) * 10) + '%)'

export function RpeGraph({ reps, rpe, target }) {
  const q = Math.min(Math.max(Math.round(rpe * 2) / 2, 6), 10)
  const r = Math.min(Math.max(Math.round(reps), 1), 12)
  const sel = pct(q, r)
  const ticks = [60, 65, 70, 75, 80, 85, 90, 95, 100]
  return (
    <svg className="tl-graph" viewBox={'0 0 ' + W + ' ' + H} role="img" aria-label="نمودار درصد از 1RM بر اساس تعداد تکرار و RPE">
      {ticks.map((t) => (
        <g key={t}>
          <line x1={M.l} x2={W - M.r} y1={y(t)} y2={y(t)} className="tl-graph__grid" />
          <text x={M.l - 8} y={y(t) + 4} textAnchor="end" className="tl-graph__tick">{t}%</text>
        </g>
      ))}
      {REPS.map((n) => (
        <g key={n}>
          <line x1={x(n)} x2={x(n)} y1={M.t} y2={H - M.b} className="tl-graph__grid tl-graph__grid--v" />
          <text x={x(n)} y={H - M.b + 18} textAnchor="middle" className="tl-graph__tick">{n}</text>
        </g>
      ))}
      <text x={(M.l + W - M.r) / 2} y={H - 6} textAnchor="middle" className="tl-graph__axis">تعداد تکرار</text>
      <text transform={'translate(12 ' + (M.t + (H - M.t - M.b) / 2) + ') rotate(-90)'} textAnchor="middle" className="tl-graph__axis">درصد از 1RM</text>
      {RPE_ROWS.map((row) => {
        const active = row === q
        const d = CHART[row].map((p, i) => (i ? 'L' : 'M') + x(i + 1).toFixed(1) + ' ' + y(p).toFixed(1)).join(' ')
        return (
          <g key={row} opacity={active ? 1 : 0.5}>
            <path d={d} fill="none" stroke={lineColor(row)} strokeWidth={active ? 4 : 2} strokeLinejoin="round" strokeLinecap="round" />
            {(active || Number.isInteger(row)) && (
              <text x={x(12) + 8} y={y(CHART[row][11]) + 4} className="tl-graph__label" fill={lineColor(row)} fontWeight={active ? 900 : 700}>
                RPE {row}
              </text>
            )}
          </g>
        )
      })}
      {target && (
        <g>
          <circle cx={x(target.reps)} cy={y(target.pct)} r="9" fill="none" stroke="#000" strokeWidth="2.5" strokeDasharray="4 3" />
          <text x={x(target.reps)} y={y(target.pct) - 14} textAnchor="middle" className="tl-graph__point">هدف</text>
        </g>
      )}
      <line x1={x(r)} x2={x(r)} y1={y(sel)} y2={H - M.b} stroke="#000" strokeWidth="1.5" strokeDasharray="3 3" />
      <circle cx={x(r)} cy={y(sel)} r="7" fill="#fde047" stroke="#000" strokeWidth="3" />
      <text x={x(r)} y={y(sel) + 24} textAnchor="middle" className="tl-graph__point" dir="ltr">{sel}%</text>
    </svg>
  )
}

export function RpeTable({ reps, rpe, target }) {
  const q = Math.min(Math.max(Math.round(rpe * 2) / 2, 6), 10)
  const r = Math.min(Math.max(Math.round(reps), 1), 12)
  const tq = target ? Math.min(Math.max(Math.round(target.rpe * 2) / 2, 6), 10) : null
  return (
    <div className="tl-tablewrap">
      <table className="tl-table tl-table--chart" dir="ltr">
        <thead>
          <tr>
            <th>RPE</th>
            <th>RIR</th>
            {REPS.map((n) => <th key={n}>{n}</th>)}
          </tr>
        </thead>
        <tbody>
          {RPE_ROWS.map((row) => (
            <tr key={row} className={row === q ? 'is-row' : ''}>
              <th>{row}</th>
              <th>{RIR_LABEL[row]}</th>
              {CHART[row].map((p, i) => (
                <td
                  key={i}
                  style={{ background: cellColor(p) }}
                  className={(row === q && i + 1 === r ? 'is-sel ' : '') + (target && row === tq && i + 1 === target.reps ? 'is-target' : '')}
                >
                  {p}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
